import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, History, RotateCcw, FileText, Save, Shield, GitCompare, X } from "lucide-react";
import { toast } from "sonner";
import { cmsList, cmsVersions, cmsVersionDetail, cmsRestore, listRestorePoints, createRestorePoint, restoreFullSite } from "@/lib/api";

// Flatten a CMS page into readable lines for a simple before/after diff.
function serialize(page) {
  if (!page) return [];
  const L = [];
  L.push(`Title: ${page.title || ""}`);
  L.push(`Slug: ${page.slug || ""}`);
  L.push(`Status: ${page.status || ""}`);
  if (page.excerpt) L.push(`Excerpt: ${page.excerpt}`);
  const seo = page.seo || {};
  if (seo.title) L.push(`SEO Title: ${seo.title}`);
  if (seo.description) L.push(`SEO Description: ${seo.description}`);
  (page.blocks || []).forEach((b, i) => {
    const t = b.text ?? b.content ?? b.title ?? b.heading ?? "";
    L.push(`[${b.type || "block"} #${i + 1}] ${typeof t === "string" ? t : JSON.stringify(t)}`.trim());
  });
  return L;
}

function DiffView({ before, after }) {
  const beforeL = serialize(before), afterL = serialize(after);
  const bSet = new Set(beforeL), aSet = new Set(afterL);
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="version-diff">
      <div>
        <div className="text-[11px] font-bold uppercase text-slate-400 mb-1.5">This version (restore to)</div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12px] font-mono space-y-0.5 max-h-[50vh] overflow-y-auto">
          {beforeL.map((l, i) => <div key={i} className={aSet.has(l) ? "text-slate-500" : "bg-rose-50 text-rose-700 rounded px-1"}>{l || "\u00A0"}</div>)}
        </div>
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase text-slate-400 mb-1.5">Current (live now)</div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12px] font-mono space-y-0.5 max-h-[50vh] overflow-y-auto">
          {afterL.map((l, i) => <div key={i} className={bSet.has(l) ? "text-slate-500" : "bg-emerald-50 text-emerald-700 rounded px-1"}>{l || "\u00A0"}</div>)}
        </div>
      </div>
    </div>
  );
}

export default function AdminVersions() {
  const [pages, setPages] = useState(null);
  const [sel, setSel] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingV, setLoadingV] = useState(false);
  const [points, setPoints] = useState([]);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [diff, setDiff] = useState(null); // { before, after } or "loading"

  useEffect(() => {
    cmsList().then((d) => setPages(d || [])).catch(() => toast.error("Failed to load — admin login required"));
    loadPoints();
  }, []);
  const loadPoints = () => listRestorePoints().then((d) => setPoints(d.restore_points || [])).catch(() => {});

  const openPage = (p) => {
    setSel(p); setLoadingV(true);
    cmsVersions(p.id).then((d) => setVersions(d.versions || [])).catch(() => setVersions([])).finally(() => setLoadingV(false));
  };
  const restore = async (v) => {
    if (!window.confirm("Restore this page to the selected version? Your current version is saved first.")) return;
    try { await cmsRestore(sel.id, v.id); toast.success("Page restored"); openPage(sel); } catch { toast.error("Restore failed"); }
  };
  const openDiff = async (v) => {
    setDiff("loading");
    try { const d = await cmsVersionDetail(sel.id, v.id); setDiff({ before: d.version?.snapshot, after: d.current }); }
    catch { toast.error("Could not load diff"); setDiff(null); }
  };
  const makePoint = async () => {
    setBusy(true);
    try { await createRestorePoint(label.trim()); setLabel(""); toast.success("Restore point created"); loadPoints(); }
    catch { toast.error("Failed to create restore point"); } finally { setBusy(false); }
  };
  const restoreSite = async (rp) => {
    if (!window.confirm(`Restore the ENTIRE site to "${rp.label}"? Current pages are snapshotted first.`)) return;
    try { const r = await restoreFullSite(rp.id); toast.success(`Restored ${r.restored} pages`); loadPoints(); if (sel) openPage(sel); }
    catch { toast.error("Site restore failed"); }
  };

  if (!pages) return <main className="min-h-screen bg-slate-50 grid place-items-center"><Loader2 className="animate-spin text-brand" size={26} /></main>;

  return (
    <main className="min-h-screen bg-slate-50 pb-24" data-testid="admin-versions">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
        <div className="flex items-center gap-2"><History size={22} className="text-brand" /><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Version History &amp; Rollback</h1></div>
        <p className="text-sm text-slate-500 mt-2">Every page save is snapshotted. Roll a single page back, or restore the whole site to a saved point.</p>

        {/* Site restore points */}
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5 mt-6" data-testid="restore-points">
          <div className="flex items-center gap-2 mb-3"><Shield size={16} className="text-brand" /><span className="text-sm font-semibold text-slate-800">Full-site restore points</span></div>
          <div className="flex gap-2">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Before homepage rework)" data-testid="restore-point-label"
              className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-brand outline-none" />
            <button onClick={makePoint} disabled={busy} data-testid="create-restore-point"
              className="inline-flex items-center gap-1.5 bg-brand text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Create</button>
          </div>
          <div className="mt-3 space-y-2">
            {points.length === 0 && <div className="text-sm text-slate-400">No restore points yet.</div>}
            {points.map((rp) => (
              <div key={rp.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`restore-point-${rp.id}`}>
                <div className="min-w-0"><div className="text-sm font-medium text-slate-700 truncate">{rp.label}{rp.auto ? " (auto)" : ""}</div>
                  <div className="text-[11px] text-slate-400">{rp.page_count} pages · {rp.created_at?.slice(0, 16).replace("T", " ")}</div></div>
                <button onClick={() => restoreSite(rp)} data-testid={`restore-site-${rp.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 border border-amber-200 rounded-lg px-3 py-1.5 hover:bg-amber-50"><RotateCcw size={13} /> Restore site</button>
              </div>
            ))}
          </div>
        </div>

        {/* Per-page versions */}
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-4">
            <div className="text-sm font-semibold text-slate-800 mb-2">Pages</div>
            <div className="space-y-1 max-h-[420px] overflow-y-auto" data-testid="versions-page-list">
              {pages.map((p) => (
                <button key={p.id} onClick={() => openPage(p)} data-testid={`version-page-${p.id}`}
                  className={`w-full text-left rounded-lg px-3 py-2 text-sm flex items-center gap-2 transition-colors ${sel?.id === p.id ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"}`}>
                  <FileText size={14} className="shrink-0" /> <span className="truncate">{p.title || p.slug}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-4">
            <div className="text-sm font-semibold text-slate-800 mb-2">{sel ? `Versions — ${sel.title || sel.slug}` : "Select a page"}</div>
            {!sel ? <div className="text-sm text-slate-400 py-8 text-center">Pick a page to see its history.</div>
              : loadingV ? <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-brand" size={22} /></div>
              : versions.length === 0 ? <div className="text-sm text-slate-400 py-8 text-center">No saved versions yet. Versions are captured each time this page is edited.</div>
              : (
                <div className="space-y-2 max-h-[380px] overflow-y-auto" data-testid="versions-list">
                  {versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`version-${v.id}`}>
                      <div className="min-w-0"><div className="text-[13px] font-medium text-slate-700 truncate">{v.title || "(untitled)"}{v.note ? ` · ${v.note}` : ""}</div>
                        <div className="text-[11px] text-slate-400">{v.saved_at?.slice(0, 16).replace("T", " ")} · {v.saved_by}</div></div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => openDiff(v)} data-testid={`version-diff-${v.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-100"><GitCompare size={13} /> Diff</button>
                        <button onClick={() => restore(v)} data-testid={`version-restore-${v.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50"><RotateCcw size={13} /> Restore</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Diff modal */}
      {diff && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" onClick={() => setDiff(null)} data-testid="version-diff-modal">
          <div className="bg-white rounded-2xl soft-shadow w-full max-w-3xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 font-heading font-bold text-slate-900"><GitCompare size={18} className="text-brand" /> Compare version → current</div>
              <button onClick={() => setDiff(null)} className="text-slate-400 hover:text-slate-700" data-testid="version-diff-close"><X size={18} /></button>
            </div>
            {diff === "loading" ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-brand" size={22} /></div>
              : <DiffView before={diff.before} after={diff.after} />}
            <div className="mt-3 text-[11px] text-slate-400 flex gap-4"><span><span className="inline-block h-2 w-2 rounded-sm bg-rose-200 mr-1" />only in this version</span><span><span className="inline-block h-2 w-2 rounded-sm bg-emerald-200 mr-1" />only in current</span></div>
          </div>
        </div>
      )}
    </main>
  );
}
